// @flow
import ResourceModel from "../base/ResourceModel";
import LinkModel from "../links/LinkModel";
import UserServicesModel from "../user/UserServicesModel";

import { NotAllowedUriException } from "../../exceptions";

import { DEFAULT_AUTHENTICATION_TYPE } from "../../constants";

import Cache from "../../utils/browser/Cache";

import type { ModularUIModel, AuthenticationType } from "../types";
import type { ModularUIResponse } from "../../modularui";
import type LinkCollection from "../links/LinkCollection";
import type Href from "../href/Href";

/**
 * The Application model
 */
export default class ApplicationModel extends ResourceModel {
  _userServices: ?UserServicesModel;

  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    // store authentication type in session cache, to make it available when application is no longer available (when not permitted for example)
    Cache.setItem("primaryAuthenticationType", this.primaryAuthenticationType);
  }

  /**
   * Retrieve type of model
   */
  get type(): string {
    return "Application";
  }

  /**
   */
  static get modelName(): string {
    return "ApplicationModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    return (
      data.contributions.resourcetype &&
      data.contributions.resourcetype === "Application"
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    const userService = this.links.getLinkByKey("UserServices");
    if (userService && userService.href.path === "/login") {
      throw new NotAllowedUriException(
        "The user service (Login panel) should not have the uri '/login', use a different URI because this uri matches the login service.",
      );
    }

    return userService ? [userService] : [];
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>) {
    const userServiceModel = models.find(
      (model) => model.type === "UserServices",
    );

    if (userServiceModel) {
      this.userServices = userServiceModel;
    }
  }

  /**
   * Getting the label of the application
   */
  get label(): string {
    return this.getContribution("label", "");
  }

  /**
   * Getting the authentication types of the application
   */
  get authenticationTypes(): Array<AuthenticationType> {
    const authenticationTypes = this.getContribution("security", [
      DEFAULT_AUTHENTICATION_TYPE,
    ]);

    if (authenticationTypes) {
      //put primary first
      return authenticationTypes?.sort(
        (a: AuthenticationType, b: AuthenticationType) => {
          if (a.isPrimary === b.isPrimary) {
            return 0;
          }

          return a.isPrimary ? -1 : 1;
        },
      );
    }

    return [DEFAULT_AUTHENTICATION_TYPE];
  }

  /**
   * Returns the primary authentication type
   * @returns {AuthenticationType}
   */
  get primaryAuthenticationType(): AuthenticationType {
    return this.authenticationTypes[0];
  }

  /**
   * Getting the tab links
   */
  get tabs(): LinkCollection {
    return this.links.getLinksByGroup("tab");
  }

  /**
   * Get modelcatalog link
   */
  get modelcatalog(): LinkModel {
    return LinkModel.create(
      "modelcatalog",
      "/modelcatalog",
      "Model catalog",
      this.modelOptions,
    );
  }

  /**
   * Set the userservices for this application
   */
  set userServices(model: ?ModularUIModel) {
    this._userServices = model instanceof UserServicesModel ? model : null;
  }

  /**
   * returns the userservices configured for this application
   */
  get userServices(): ?UserServicesModel {
    return this._userServices ? this._userServices : null;
  }

  /**
   * Indicates if the user is logged in
   */
  get isLoggedIn(): boolean {
    return this.userServices?.isLoggedIn ?? false;
  }

  /**
   * Retrieve link to the user information,
   * only available when the user services are available
   */
  get userHref(): Href | null {
    if (this.userServices?.userLink) {
      return this.userServices.userLink.href;
    }

    return null;
  }

  /**
   */
  get userMustChangePassword(): boolean {
    return this.userServices?.mustChangePassword ?? false;
  }
}
